import { useEffect, useRef } from 'react';
import { useLifely } from '../../context/LifelyContext';

/**
 * MemberstackInitializer - Initializes Memberstack SDK and handles auth events
 * Checks if user is already logged in on page load
 */
const MemberstackInitializer = () => {
  const { login, logout, isAuthenticated } = useLifely();
  const initialized = useRef(false);

  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (initialized.current) return;

    const initMemberstack = async () => {
      const memberstack = window.$memberstackDom;
      
      if (!memberstack) {
        console.log('Memberstack not loaded yet');
        return;
      }

      console.log('Memberstack found, initializing...');
      initialized.current = true;

      // Check if user is already logged in (page refresh scenario)
      try {
        const memberResult = await memberstack.getCurrentMember();
        console.log('getCurrentMember on init:', memberResult?.data ? 'logged in' : 'not logged in');
        
        if (memberResult?.data && !isAuthenticated) {
          const member = memberResult.data;
          console.log('User already logged in on page load:', member.id);
          
          // Auto-login to our backend
          await login(member.id);
          console.log('Auto-login to backend successful');
        }
      } catch (err) {
        console.log('getCurrentMember error:', err.message);
      }

      // Listen for auth changes (logout)
      memberstack.onAuthChange(async (payload) => {
        console.log('onAuthChange:', payload?.isLoggedIn ? 'logged in' : 'logged out');
        
        if (!payload?.isLoggedIn) {
          console.log('User logged out via Memberstack');
          logout();
        }
      });

      console.log('Memberstack initialized');
    };

    // Try to initialize immediately or poll
    if (window.$memberstackDom) {
      initMemberstack();
    } else {
      const checkInterval = setInterval(() => {
        if (window.$memberstackDom) {
          clearInterval(checkInterval);
          initMemberstack();
        }
      }, 100);

      setTimeout(() => clearInterval(checkInterval), 10000);
      return () => clearInterval(checkInterval);
    }
  }, [login, logout, isAuthenticated]);

  return null;
};

export default MemberstackInitializer;
